import * as React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import { GetString } from '../../StringResourceManager';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import {
  Upload,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
} from '@progress/kendo-react-upload';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { CategoryCountRequest } from '../../../Interfaces/Assets/CategoryCountRequest';
import { postJsonData } from '../../../utils/utils';
import { CategoryCountResponseEntry } from '../../../Interfaces/Assets/CategoryCountResponseEntry';
import clsx from 'clsx';
import {
  FirmwareFileAndChecksumMap,
  MultipleFirmwareRecords,
} from '../../../Interfaces/Devices/FirmwareInfo';
import {
  calculateChecksum,
  clearChecksum,
  clearConfirmationMessage,
  uploadFirmwareFile,
} from '../../../store/Devices/AssetStatesActions';
import { Dispatch } from 'redux';
import { useEffect, useRef } from 'react';
import { Button as DialogButton } from '@mui/material';
import DynamicComboBox from '../../DynamicComboBox';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addNewFirmwareGridLayout: {
      fontSize: '20px',
      padding: '50px',
      fontWeight: 'bold',
    },
    addNewFirmwarePromptLabel: {
      border: '1px solid #ccc',
      textAlign: 'center',
      height: 60,
      paddingTop: theme.spacing(2),
    },
    addNewFirmwarePromptValue: {
      border: '1px solid #ccc',
      textAlign: 'center',
      color: 'darkblue',
      fontSize: '20px',
      height: 60,
      lineHeight: '60px',
      alignItems: 'center',
    },
    addNewFirmwareFileUploadRow: {
      height: '200px',
      overflowY: 'auto',
    },
    firmwareDialog: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      flexWrap: 'wrap',
    },
    firmwareDialogTitle: {
      minWidth: '650px',
      fontSize: '24px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    firmwareDialogButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 10,
    },
    firmwareClosePromptButtonLeft: {
      height: '40px',
      fontSize: '20px',
      width: '150px',
      marginRight: 10,
    },
    firmwareClosePromptButtonRight: {
      height: '40px',
      fontSize: '20px',
      width: '150px',
      marginLeft: 10,
    },
  })
);

interface AddNewFirmwareDialogProps {
  onSave: (firmwareInfo?: MultipleFirmwareRecords) => any;
  onCancel: (event: any) => any;
}

export const AddNewFirmwareDialog = (props: AddNewFirmwareDialogProps) => {
  const classes = useStyles();

  const FIRMWARE_MENU_ADD_NEW_FIRMWARE = GetString(
    'FIRMWARE_MENU_ADD_NEW_FIRMWARE'
  );
  const COLUMN_TITLE_DEVICE_CATEGORY = GetString(
    'COLUMN_TITLE_DEVICE_CATEGORY'
  );
  const COLUMN_TITLE_DEVICE_MODEL = GetString('COLUMN_TITLE_DEVICE_MODEL');
  const COLUMN_TITLE_REGION_INFO = GetString('COLUMN_TITLE_REGION_INFO');
  const COLUMN_TITLE_FIRMWARE_FILE = GetString('COLUMN_TITLE_FIRMWARE_FILE');
  const COLUMN_TITLE_FIRMWARE_CHECKSUM = GetString(
    'COLUMN_TITLE_FIRMWARE_CHECKSUM'
  );
  const SAVE = GetString('SAVE_BUTTON');
  const CANCEL = GetString('CANCEL_BUTTON');
  const YES = GetString('YES');
  const NO = GetString('NO');
  const FIRMWARE_UPLOAD_CONFIRMATION_TITLE = GetString(
    'FIRMWARE_UPLOAD_CONFIRMATION_TITLE'
  );
  const FIRMWARE_UPLOAD_CONFIRMATION_MESSAGE = GetString(
    'FIRMWARE_UPLOAD_CONFIRMATION_MESSAGE'
  );

  const validExtensions: string[] = ['.zip', '.reg', '.rf', '.dat'];

  const dispatch: Dispatch<any> = useDispatch();

  const uploadRef = useRef<Upload>(null);

  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState(false);

  const [selectedFiles, setSelectedFiles] = React.useState<any[]>([]);

  const [selectedFileData, setSelectedFileData] = React.useState<FormData>();

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const assetCategories: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.assetCategories
  );

  const fileChecksumMap: FirmwareFileAndChecksumMap[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.fileChecksumMap
  );

  const confirmationMessage: string = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.confirmationMessage
  );

  const [selectedMultipleFirmwareRecords, setselectedMultipleFirmwareRecords] =
    React.useState<MultipleFirmwareRecords>();

  const [deviceModels, setDeviceModels] = React.useState<string[]>();
  const [deviceRegionInfo, setDeviceRegionInfo] = React.useState<string[]>();
  const [hasInvalidFiles, setHasInvalidFiles] = React.useState(false);

  const checkInvalidFiles = (files: any[]) => {
    const hasInvalid = files.some(file => {
      const name = file.name.toLowerCase();
      return !validExtensions.some(ext => name.endsWith(ext));
    });

    setHasInvalidFiles(hasInvalid);
  };

  const onChangeAssetCategory = async (event: any) => {
    let deviceModelRequest: CategoryCountRequest = {
      Category: event.target.value,
      Name: 'Model',
    };
    let deviceRegionInfoRequest: CategoryCountRequest = {
      Category: event.target.value,
      Name: 'RegionInfo',
    };
    if (event.target.value !== null) {
      let modelsResponse = await fetchDeviceInfo(deviceModelRequest);
      let models: string[] = modelsResponse.map(d => d.Value);
      let regionInfoResponse = await fetchDeviceInfo(deviceRegionInfoRequest);
      let regionInfo: string[] = regionInfoResponse.map(d => d.Value);
      regionInfo = ['N/A', ...regionInfo];
      setDeviceModels(models);
      setDeviceRegionInfo(regionInfo);
    } else {
      setDeviceModels([]);
      setDeviceRegionInfo([]);
    }

    let firmwareInfo: MultipleFirmwareRecords = {
      ID: 0,
      DeviceCategory: deviceModelRequest.Category,
      DeviceModel: '',
      RegionInfo: '',
      FirmwareFileAndChecksumMap:
        selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap ?? [],
      FilePath: deviceModelRequest.Category,
      ImportDate: new Date(),
      FirmwareActive: true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  };

  const fetchDeviceInfo = async (
    categoryCountRequest: CategoryCountRequest
  ): Promise<CategoryCountResponseEntry[]> => {
    const response = await postJsonData(
      `assets/GetCategoryCount`,
      token,
      categoryCountRequest
    );
    return response as CategoryCountResponseEntry[];
  };

  const onChangeDeviceModel = async (event: any) => {
    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: event.target.value,
      RegionInfo: '',
      FirmwareFileAndChecksumMap:
        selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap ?? [],
      FilePath: `${selectedMultipleFirmwareRecords?.DeviceCategory}/${event.target.value}`,
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  };

  const onChangeRegionInfo = async (event: any) => {
    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: selectedMultipleFirmwareRecords?.DeviceModel ?? '',
      RegionInfo: event.target.value,
      FirmwareFileAndChecksumMap:
        selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap ?? [],
      FilePath: `${selectedMultipleFirmwareRecords?.DeviceCategory}/${selectedMultipleFirmwareRecords?.DeviceModel}/${event.target.value}/`,
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  };

  const validateSelectedFiles = (event: UploadOnAddEvent) => {
    const newFiles = event.newState;
    checkInvalidFiles(newFiles);

    const existingFileNames = new Set(
      selectedFiles.map(file => file.name.toLowerCase())
    );
    const newUniqueFiles = newFiles.filter(file => {
      const name = file.name.toLowerCase();
      return !existingFileNames.has(name);
    });

    const updatedSelectedFiles = [...selectedFiles, ...newUniqueFiles];
    setSelectedFiles(updatedSelectedFiles);

    const uniqueAndValidFiles = newFiles.filter(file => {
      const name = file.name.toLowerCase();
      const hasValidExt = validExtensions.some(ext => name.endsWith(ext));
      const isDuplicate = existingFileNames.has(name);
      return hasValidExt && !isDuplicate;
    });

    const validFiles = [...selectedFiles, ...uniqueAndValidFiles].filter(file =>
      validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    );

    if (uniqueAndValidFiles.length === 0) return;

    const formData = new FormData();
    validFiles.forEach(file => {
      const rawFile = file.getRawFile ? file.getRawFile() : undefined;
      if (rawFile) {
        formData.append('file', rawFile);
      }
    });
    calculateChecksum(dispatch, formData);
    setSelectedFileData(formData);

    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: selectedMultipleFirmwareRecords?.DeviceModel ?? '',
      RegionInfo: selectedMultipleFirmwareRecords?.RegionInfo ?? '',
      FirmwareFileAndChecksumMap: fileChecksumMap,
      FilePath: selectedMultipleFirmwareRecords?.FilePath ?? '',
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  };

  const removeSelectedFiles = (event: UploadOnRemoveEvent) => {
    const updatedSelectedFiles = event.newState;
    const removedFiles = event.affectedFiles;
    setSelectedFiles(updatedSelectedFiles);
    checkInvalidFiles(updatedSelectedFiles);

    const updatedValidFiles = updatedSelectedFiles.filter(file => {
      const name = file.name.toLowerCase();
      return validExtensions.some(ext => name.endsWith(ext));
    });

    const formData = new FormData();
    updatedValidFiles.forEach(file => {
      const rawFile = file.getRawFile ? file.getRawFile() : undefined;
      if (rawFile) {
        formData.append('file', rawFile);
      }
    });

    setSelectedFileData(formData);

    const updatedFirmwareFileAndChecksum =
      selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap.filter(
        entry => !removedFiles.some(removed => removed.name === entry.FileName)
      ) ?? [];

    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: selectedMultipleFirmwareRecords?.DeviceModel ?? '',
      RegionInfo: selectedMultipleFirmwareRecords?.RegionInfo ?? '',
      FirmwareFileAndChecksumMap: updatedFirmwareFileAndChecksum,
      FilePath: selectedMultipleFirmwareRecords?.FilePath ?? '',
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  };

  const handleUploadClick = () => {
    if (!selectedMultipleFirmwareRecords) return;
    const updatedRegionInfo =
      selectedMultipleFirmwareRecords.RegionInfo === 'N/A'
        ? 'NA'
        : selectedMultipleFirmwareRecords.RegionInfo;
    const updatedFilePath = `${selectedMultipleFirmwareRecords?.DeviceCategory}/${selectedMultipleFirmwareRecords?.DeviceModel}/${updatedRegionInfo}/`;

    const updatedMultipleFirmwareRecords: MultipleFirmwareRecords = {
      ...selectedMultipleFirmwareRecords,
      RegionInfo: updatedRegionInfo,
      FilePath: updatedFilePath,
    };

    selectedFileData?.append(
      'filepath',
      updatedMultipleFirmwareRecords?.FilePath ?? ''
    );
    uploadFirmwareFile(dispatch, selectedFileData);
    setShowConfirmationDialog(false);
    clearConfirmationMessage(dispatch);
    props.onSave(updatedMultipleFirmwareRecords);
  };

  const handleSave = () => {
    if (confirmationMessage) {
      setShowConfirmationDialog(true);
    }
  };

  const handleClose = () => {
    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: selectedMultipleFirmwareRecords?.DeviceModel ?? '',
      RegionInfo: selectedMultipleFirmwareRecords?.RegionInfo ?? '',
      FirmwareFileAndChecksumMap: [],
      FilePath: selectedMultipleFirmwareRecords?.FilePath ?? '',
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
    setShowConfirmationDialog(false);
    clearConfirmationMessage(dispatch);
    clearChecksum(dispatch);
    setSelectedFiles([]);
  };

  useEffect(() => {
    let firmwareInfo: MultipleFirmwareRecords = {
      ID: selectedMultipleFirmwareRecords?.ID ?? 0,
      DeviceCategory: selectedMultipleFirmwareRecords?.DeviceCategory ?? '',
      DeviceModel: selectedMultipleFirmwareRecords?.DeviceModel ?? '',
      RegionInfo: selectedMultipleFirmwareRecords?.RegionInfo ?? '',
      FirmwareFileAndChecksumMap: fileChecksumMap,
      FilePath: selectedMultipleFirmwareRecords?.FilePath ?? '',
      ImportDate: new Date(),
      FirmwareActive: selectedMultipleFirmwareRecords?.FirmwareActive ?? true,
    };
    setselectedMultipleFirmwareRecords(firmwareInfo);
  }, [fileChecksumMap]);

  return (
    <Dialog onClose={props.onCancel}>
      <div className={classes.firmwareDialog}>
        <div className={classes.firmwareDialogTitle}>
          {FIRMWARE_MENU_ADD_NEW_FIRMWARE}
        </div>
        <GridLayout
          cols={[{ width: 200 }, { width: 300 }]}
          className={classes.addNewFirmwareGridLayout}
          align={{ horizontal: 'stretch' }}
        >
          <GridLayoutItem
            col={1}
            colSpan={1}
            className={classes.addNewFirmwarePromptLabel}
          >
            {COLUMN_TITLE_DEVICE_CATEGORY}
          </GridLayoutItem>
          <GridLayoutItem
            col={2}
            colSpan={1}
            className={classes.addNewFirmwarePromptValue}
          >
            <DynamicComboBox
              onChange={onChangeAssetCategory}
              data={assetCategories}
              value={selectedMultipleFirmwareRecords?.DeviceCategory ?? ''}
              placeholder={COLUMN_TITLE_DEVICE_CATEGORY}
            />
          </GridLayoutItem>

          <GridLayoutItem
            col={1}
            colSpan={1}
            className={classes.addNewFirmwarePromptLabel}
          >
            {COLUMN_TITLE_DEVICE_MODEL}
          </GridLayoutItem>
          <GridLayoutItem
            col={2}
            colSpan={1}
            className={classes.addNewFirmwarePromptValue}
          >
            <DynamicComboBox
              onChange={onChangeDeviceModel}
              data={deviceModels}
              value={selectedMultipleFirmwareRecords?.DeviceModel ?? ''}
              placeholder={COLUMN_TITLE_DEVICE_MODEL}
            />
          </GridLayoutItem>

          <GridLayoutItem
            col={1}
            colSpan={1}
            className={classes.addNewFirmwarePromptLabel}
          >
            {COLUMN_TITLE_REGION_INFO}
          </GridLayoutItem>
          <GridLayoutItem
            col={2}
            colSpan={1}
            className={classes.addNewFirmwarePromptValue}
          >
            <DynamicComboBox
              onChange={onChangeRegionInfo}
              data={deviceRegionInfo}
              value={selectedMultipleFirmwareRecords?.RegionInfo ?? ''}
              placeholder={COLUMN_TITLE_REGION_INFO}
            />
          </GridLayoutItem>

          <GridLayoutItem
            col={1}
            colSpan={1}
            className={clsx(
              classes.addNewFirmwarePromptLabel,
              classes.addNewFirmwareFileUploadRow
            )}
          >
            {COLUMN_TITLE_FIRMWARE_FILE}
          </GridLayoutItem>
          <GridLayoutItem
            col={2}
            colSpan={1}
            className={clsx(
              classes.addNewFirmwarePromptValue,
              classes.addNewFirmwareFileUploadRow
            )}
          >
            <Upload
              batch={false}
              ref={uploadRef}
              restrictions={{
                allowedExtensions: validExtensions,
              }}
              files={selectedFiles}
              autoUpload={false}
              showActionButtons={false}
              multiple={true}
              onAdd={validateSelectedFiles}
              onRemove={removeSelectedFiles}
            />
          </GridLayoutItem>
        </GridLayout>
        <DialogActionsBar>
          <div className={classes.firmwareDialogButtonContainer}>
            <Button
              themeColor="primary"
              className={classes.firmwareClosePromptButtonLeft}
              onClick={handleSave}
              disabled={
                !selectedMultipleFirmwareRecords?.DeviceCategory ||
                !selectedMultipleFirmwareRecords?.DeviceModel ||
                !selectedMultipleFirmwareRecords?.RegionInfo ||
                selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap
                  .length === 0 ||
                hasInvalidFiles
              }
            >
              {SAVE}
            </Button>
            <Button
              themeColor="primary"
              className={classes.firmwareClosePromptButtonRight}
              onClick={props.onCancel}
            >
              {CANCEL}
            </Button>
          </div>
        </DialogActionsBar>
      </div>
      {showConfirmationDialog && (
        <Dialog
          onClose={handleClose}
          title={FIRMWARE_UPLOAD_CONFIRMATION_TITLE}
        >
          <Typography variant="body1">
            {confirmationMessage === 'add' &&
              FIRMWARE_UPLOAD_CONFIRMATION_MESSAGE}
          </Typography>
          <div>
            <section style={{ float: 'left' }}>
              {selectedMultipleFirmwareRecords?.FirmwareFileAndChecksumMap?.map(
                (file, index) => (
                  <p key={index}>
                    <strong>{COLUMN_TITLE_FIRMWARE_CHECKSUM}:</strong>{' '}
                    {file.FileChecksum}
                    <br></br>
                    <strong>{COLUMN_TITLE_FIRMWARE_FILE}:</strong>{' '}
                    {file.FileName}
                  </p>
                )
              )}
            </section>
          </div>
          <DialogActionsBar layout="center">
            <DialogButton
              color="primary"
              variant="contained"
              onClick={handleUploadClick}
            >
              {YES}
            </DialogButton>
            <DialogButton
              color="primary"
              variant="contained"
              onClick={handleClose}
            >
              {NO}
            </DialogButton>
          </DialogActionsBar>
        </Dialog>
      )}
    </Dialog>
  );
};
